import { executeNavigate } from '@common/utils/handleActions/ActionItem/navigate';
import { useAppSelector } from '@common/redux/hooks';
import { profileSelector } from '@common/redux/selectors/auth';
import { getActions } from '@common/utils/handleActions/func/helps';
import { IQuestionItem, IQuestionnaire } from '@nocode/types';
import { useCallback, useState, useEffect } from 'react';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { Colors } from 'react-native-paper';
import { QUESTION_CONTROL_TYPE } from '@nocode/types/questionnaire.type';

export const DISPLAY_QUESTIONS = [
  {
    id: 'radio',
    index: 0,
    control: QUESTION_CONTROL_TYPE.RADIO,
    question: '日本の首都はどこですか？？',
    options: [
      { id: '1', label: '京都', value: 'kyoto' },
      { id: '2', label: '大阪', value: 'osaka' },
      { id: '3', label: '長野', value: 'nagano' },
      { id: '4', label: '東京', value: 'tokyo' },
    ],
  },
  {
    id: 'dropdown',
    index: 1,
    control: QUESTION_CONTROL_TYPE.DROPDOWN,
    question: '日本の首都はどこですか？?',
    imageUri: 'https://picsum.photos/seed/picsum/300/200',
    options: [
      { id: '1', label: '京都', value: 'kyoto' },
      { id: '2', label: '大阪', value: 'osaka' },
      { id: '3', label: '長野', value: 'nagano' },
      { id: '4', label: '東京', value: 'tokyo' },
    ],
  },
  {
    id: 'input',
    index: 2,
    control: QUESTION_CONTROL_TYPE.INPUT,
    question: '日本の首都はどこですか?',
  },
  {
    id: 'input',
    index: 2,
    control: QUESTION_CONTROL_TYPE.TEXTAREA,
    question: '日本の首都はどこですか?',
  },
  {
    id: 'checkbox',
    index: 3,
    control: QUESTION_CONTROL_TYPE.CHECKBOX,
    question: '日本の首都はどこですか？?',
    options: [
      { id: '1', label: '京都', value: 'kyoto' },
      { id: '2', label: '大阪', value: 'osaka' },
      { id: '3', label: '長野', value: 'nagano' },
      { id: '4', label: '東京', value: 'tokyo' },
    ],
  },
  {
    id: 'date',
    index: 4,
    control: QUESTION_CONTROL_TYPE.DATE,
    type: 'number' as IQuestionItem['type'],
    question: '生年月日を教えてください',
  },
  {
    id: 'matrix',
    index: 5,
    control: QUESTION_CONTROL_TYPE.MATRIX,
    type: 'text' as IQuestionItem['type'],
    multiline: true,
    question: 'このアンケートを評価する',
    options: [
      { id: '1', label: '1', value: 1 },
      { id: '2', label: '2', value: 2 },
      { id: '3', label: '3', value: 3 },
      { id: '4', label: '4', value: 4 },
      { id: '5', label: '5', value: 5 },
    ],
  },
];

export const INIT_PREVIEW = [
  {
    index: 0,
    control: 'input',
    question: 'PREVIEW ONLY',
  },
];

const generateFormInstanceId = () =>
  `form_${Math.random().toString(36).substring(2, 11)}`;

const useQuestionnaire = ({
  configs,
  methods,
  props,
  filteredData,
}: {
  configs: Record<string, unknown>;
  methods: UseFormReturn<FieldValues, any, undefined>;
  props: IQuestionnaire;
  filteredData?: IQuestionItem[];
}) => {
  const [formInstanceId] = useState(generateFormInstanceId());
  const { onPress, actions, id, categories, questions } = props;
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [multiWorkFlow, setMultiWorkFlow] = useState<any>([]);
  const [navigateAction, setNavigateAction] = useState(null);
  const [snackbar, setSnackbar] = useState({
    visible: false,
    message: '',
    type: 'info',
    style: { backgroundColor: Colors.blue500 },
    duration: 10000,
    showAction: true,
  });
  const profile = useAppSelector(profileSelector);

  // Will reset if there are other multiple forms
  useEffect(() => {
    setCurrentIndex(0);
    return () => {
      setNavigateAction(null);
    };
  }, [formInstanceId, id]);

  const currentQuestion =
    filteredData &&
    filteredData.length > 0 &&
    currentIndex < filteredData.length
      ? filteredData[currentIndex]
      : null;

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, visible: false }));
  }, []);

  const showSnackbar = useCallback(
    (type: 'info' | 'success' | 'error', message: string) => {
      const snackbarConfig = {
        info: {
          style: { backgroundColor: Colors.blue500 },
          duration: 10000,
          showAction: true,
        },
        success: {
          style: { backgroundColor: Colors.green500 },
          duration: 2000,
          showAction: false,
        },
        error: {
          style: { backgroundColor: Colors.red500 },
          duration: 4000,
          showAction: true,
        },
      }[type];

      setSnackbar({
        visible: true,
        message,
        type,
        ...snackbarConfig,
      });

      if (type === 'success') {
        setTimeout(hideSnackbar, snackbarConfig.duration);
      }
    },
    [hideSnackbar]
  );

  const formatValues = useCallback(
    (values: Record<string, any>) => {
      const aggregated = { ...values };

      // Initialize category scores
      Object.assign(aggregated, {
        options: {
          categoriesPoint: {},
        },
      });

      categories?.forEach((category) => {
        if (category.id) {
          aggregated[category.id] = 0;
          aggregated.options.categoriesPoint[category.id] = 0;
        }
      });

      // Calculate weighted values
      questions.forEach((question) => {
        if (!question.weightedValueMode || !question.options?.length) return;

        const answer = question.fid ? values[question.fid] : '';
        const option = question.options.find((opt) => opt.label === answer);

        if (option?.point && option.category) {
          const points = Number(option.point);
          if (!isNaN(points)) {
            aggregated[option.category] =
              (aggregated[option.category] || 0) + points;
            aggregated.options.categoriesPoint[option.category] =
              aggregated[option.category];
          }
        }
      });

      for (const [key, value] of Object.entries(aggregated)) {
        if (Array.isArray(value)) {
          aggregated[key] = value.join(', ');
        }
      }
      // add user info
      aggregated.user = profile?.id;
      // add pageTemplateId
      aggregated.options.pageTemplateId = props.pageTemplateId;

      return aggregated;
    },
    [questions, categories]
  );

  const submitForm = useCallback(
    async (values: Record<string, any>, navigationAction: any = null) => {
      setIsSubmitting(true);
      showSnackbar('info', '送信中...');
      const aggregated = formatValues(values);
      try {
        await onPress(getActions(actions), {
          record: aggregated,
          formId: id,
        });

        showSnackbar('success', '正常に送信されました');
        // Navigate if we have a navigation action, with delay to show snackbar
        if (navigationAction) {
          // Add delay for the snackbar
          await new Promise((resolve) => setTimeout(resolve, 1500));
          const minimalMetadata = {
            currentRecordIds: {},
            web3Config: {},
            dependencies: {},
          };
          setNavigateAction(null);
          // Navigate Here
          await executeNavigate(navigationAction, minimalMetadata);
          methods.reset();
        }
      } catch (error: any) {
        console.error('Error submitting form:', error);
        showSnackbar('error', 'エラーが発生しました');
      } finally {
        setIsSubmitting(false);
      }
    },
    [onPress, id, methods, showSnackbar, formatValues, formInstanceId]
  );

  const events: any = {
    onNextButtonClick: useCallback(async () => {
      if (!filteredData || !filteredData.length) return;

      const values = methods.getValues();

      if (currentQuestion?.required) {
        const fieldValue = currentQuestion?.fid && values[currentQuestion.fid];
        if (
          fieldValue === undefined ||
          fieldValue === null ||
          fieldValue === ''
        ) {
          showSnackbar('error', '必須フィールドに入力してください');
          return;
        }
        if (
          currentQuestion.control === 'checkbox' &&
          Array.isArray(fieldValue) &&
          fieldValue.length === 0
        ) {
          showSnackbar('error', '必須フィールドに入力してください');
          return;
        }
      }

      const currentValue = currentQuestion?.fid && values[currentQuestion.fid];
      let actionToNavigate: any = null;

      if (currentQuestion?.generalTarget && !currentQuestion.conditional) {
        // Use generalTarget for direct navigation
        if (currentQuestion.generalTarget.targetType === 'screen') {
          actionToNavigate = {
            target: currentQuestion.generalTarget.id,
            id: Math.random(),
            options: {
              target: currentQuestion.generalTarget.id,
              transition: 'TRANSITION_PUSH',
            },
            actionType: 'navigate',
          };
          setNavigateAction(actionToNavigate);
          return submitForm(values, actionToNavigate);
        } else if (
          currentQuestion.generalTarget.targetType === 'question' &&
          filteredData
        ) {
          // Find the target index in the filtered data array
          const targetIndex = filteredData.findIndex(
            (q) => q.id === currentQuestion.generalTarget?.id
          );

          if (targetIndex !== -1) {
            setCurrentIndex(targetIndex);
            return;
          }
        }
      }

      // Check if current question has options with targets - for conditional questions
      else if (
        currentQuestion?.options &&
        Array.isArray(currentQuestion.options) &&
        currentQuestion.conditional
      ) {
        // Find the selected option
        let currentOption: any = currentQuestion.options.find(
          (option) =>
            option.label === currentValue || option.value === currentValue
        );

        if (currentOption && currentOption.target) {
          if (currentOption.targetType === 'screen') {
            actionToNavigate = {
              target: currentOption.target,
              id: Math.random(),
              options: {
                target: currentOption.target,
                transition: 'TRANSITION_PUSH',
              },
              actionType: 'navigate',
            };
            setNavigateAction(actionToNavigate);
            return submitForm(values, actionToNavigate);
          } else if (currentOption.targetType === 'question' && filteredData) {
            // Find the target index in the filtered data array
            const targetIndex = filteredData.findIndex(
              (q) => q.id === currentOption.target
            );

            if (targetIndex !== -1) {
              setCurrentIndex(targetIndex);
              return;
            }
          }
        }
      }

      const isLastQuestion = currentIndex >= filteredData.length - 1;
      if (isLastQuestion) {
        return submitForm(values);
      }
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
    }, [
      currentIndex,
      methods,
      currentQuestion,
      filteredData,
      submitForm,
      formInstanceId,
    ]),

    onPrevButtonClick: useCallback(() => {
      if (currentIndex > 0) {
        const prevIndex = currentIndex - 1;
        setCurrentIndex(prevIndex);
      }
    }, [currentIndex, formInstanceId]),

    onSubmit: useCallback(
      (values: Record<string, any>) => {
        return submitForm(values, navigateAction);
      },
      [submitForm, navigateAction, formInstanceId]
    ),
  };

  return {
    currentIndex,
    isSubmitting,
    snackbar,
    data,
    setMultiWorkFlow,
    hideSnackbar,
    setData,
    formInstanceId,
    ...events,
  };
};

export const useProcessQuestions = (
  questions: IQuestionItem[]
): Record<string, unknown>[] => {
  if (!questions || !Array.isArray(questions)) return [];

  const screenQuestions: any[] = [];
  questions.forEach((data) => {
    const existingIds = new Set<string | number>();
    const screenQuestions: any[] = [];
    questions.forEach((data) => {
      const { generalTarget } = data;
      if (
        generalTarget &&
        generalTarget.targetType == 'screen' &&
        !data.conditional
      ) {
        if (!existingIds.has(generalTarget.id)) {
          existingIds.add(generalTarget.id);
          screenQuestions.push({
            id: generalTarget.id,
            question: generalTarget.name,
            control: generalTarget.targetType,
            isScreen: true,
            disabled: true,
            questionId: generalTarget.questionId,
          });
        }
      }
      if (data.options) {
        data.options.forEach((option: any) => {
          if (
            option.targetType === 'screen' &&
            option.target &&
            data.conditional
          ) {
            if (!existingIds.has(option.target)) {
              existingIds.add(option.target);
              screenQuestions.push({
                id: option.target,
                question: option.name,
                control: 'screen',
                isScreen: true,
                disabled: true,
              });
            }
          }
        });
      }
    });

    if (data.options) {
      data.options.forEach((option: any) => {
        if (
          option.targetType === 'screen' &&
          option.target &&
          data.conditional
        ) {
          const existingScreen = screenQuestions.find(
            (screen) => screen.id === option.target
          );
          if (!existingScreen) {
            screenQuestions.push({
              id: option.target,
              question: option.name,
              control: 'screen',
              isScreen: true,
              disabled: true,
            });
          }
        }
      });
    }
  });

  // Merge screen questions with regular questions
  let processedQuestions = [...questions, ...screenQuestions];

  // Filter out null/undefined questions and process them
  processedQuestions = processedQuestions
    .filter((data) => data !== null && data !== undefined)
    .map((data) => {
      const question = JSON.parse(JSON.stringify(data));
      question.name = data.question || '';
      question.children = [];
      return question;
    });

  // Collect all conditional
  const allChildrenIds = new Set();
  processedQuestions.forEach((data) => {
    if (data.conditional && data.options) {
      data.options.forEach((option: any) => {
        if (option.target) {
          allChildrenIds.add(option.target);
        }
      });
    } else {
      if (data.generalTarget && !data.conditional) {
        allChildrenIds.add(data.generalTarget.id);
      }
    }
  });

  // Assign Children
  processedQuestions = processedQuestions.map((data, index, array) => {
    if (data.conditional && data.options) {
      // For conditional questions, use their targets as children
      const uniqueChildren = new Set();
      data.options.forEach((option: any) => {
        if (option.target) {
          uniqueChildren.add(option.target);
        }
      });
      data.children = Array.from(uniqueChildren);

      // Also check if the immediate next question should be a child
      if (index < array.length - 1) {
        const nextQuestion = array[index + 1];
        if (!allChildrenIds.has(nextQuestion.id) && !nextQuestion.isScreen) {
          // If the next question is not a target of any conditional and not a screen, make it a child
          data.children.push(nextQuestion.id);
        }
      }
    }

    // For questions with generalTarget
    else if (data.generalTarget && !data.conditional) {
      if (!data.children.includes(data.generalTarget.id)) {
        data.children.push(data.generalTarget.id);
      }
    }

    // Handle next question logic (for both conditional and non-conditional)
    if (index < array.length - 1 && !data.isScreen) {
      const nextQuestion = array[index + 1];
      if (
        !allChildrenIds.has(nextQuestion.id) &&
        !nextQuestion.isScreen &&
        !data.children.includes(nextQuestion.id)
      ) {
        data.children.push(nextQuestion.id);
      }
    }

    return data;
  });

  // Ensure all questions have proper names
  processedQuestions = processedQuestions.map((data) => {
    if (!data.name) {
      data.name = data.question;
    }
    return data;
  });

  return processedQuestions;
};

export default useQuestionnaire;
